/**
 * Os campos que a página do portal informou, confrontados com o material (E172.F7.T03). Cada
 * linha: o rótulo, o que a página informou, o que o material traz e o confronto. O campo que a
 * página não informa fica "não informado", sem o valor da página.
 */
import { Button, Input, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CampoConfrontadoData, ResultadoDoCampo } from '@/lib/conferenciaNoPortal';

const t = ptBR.conferenciaNoPortal;
const RESULTADOS: ResultadoDoCampo[] = ['confere', 'diverge', 'nao_informado'];

export interface LinhaDoCampo {
  rotulo: string;
  no_portal: string;
  no_material: string;
  resultado: ResultadoDoCampo;
}

export const LINHA_VAZIA: LinhaDoCampo = {
  rotulo: '',
  no_portal: '',
  no_material: '',
  resultado: 'confere',
};

/** As linhas preenchidas, como o servidor as recebe. */
export function camposDaTela(linhas: LinhaDoCampo[]): CampoConfrontadoData[] {
  return linhas
    .filter((l) => l.rotulo.trim() !== '')
    .map((l) => ({
      rotulo: l.rotulo.trim(),
      no_portal: l.resultado === 'nao_informado' ? null : l.no_portal.trim() || null,
      no_material: l.no_material.trim() || null,
      resultado: l.resultado,
    }));
}

export interface CamposConfrontadosProps {
  linhas: LinhaDoCampo[];
  onMudar: (linhas: LinhaDoCampo[]) => void;
}

export function CamposConfrontados({ linhas, onMudar }: CamposConfrontadosProps): JSX.Element {
  function mudar(i: number, parte: Partial<LinhaDoCampo>): void {
    onMudar(linhas.map((l, j) => (j === i ? { ...l, ...parte } : l)));
  }
  return (
    <fieldset className="space-y-sc-2" data-testid="portal-campos-form">
      <legend className="text-caption text-fg-dim">{t.registradas.campos}</legend>
      {linhas.map((l, i) => (
        <div key={i} className="grid grid-cols-1 gap-sc-2 sm:grid-cols-5" data-linha={i}>
          <Input
            aria-label={t.formulario.campo}
            value={l.rotulo}
            maxLength={80}
            data-campo="rotulo"
            onChange={(e) => mudar(i, { rotulo: e.target.value })}
          />
          <Input
            aria-label={t.formulario.noPortal}
            value={l.no_portal}
            maxLength={500}
            disabled={l.resultado === 'nao_informado'}
            data-campo="no_portal"
            onChange={(e) => mudar(i, { no_portal: e.target.value })}
          />
          <Input
            aria-label={t.formulario.noMaterial}
            value={l.no_material}
            maxLength={500}
            data-campo="no_material"
            onChange={(e) => mudar(i, { no_material: e.target.value })}
          />
          <Select
            aria-label={t.formulario.resultado}
            value={l.resultado}
            data-campo="resultado"
            onChange={(e) => mudar(i, { resultado: e.target.value as ResultadoDoCampo })}
          >
            {RESULTADOS.map((r) => (
              <option key={r} value={r}>
                {t.resultado[r]}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={linhas.length === 1}
            onClick={() => onMudar(linhas.filter((_, j) => j !== i))}
          >
            {t.formulario.removerCampo}
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        data-testid="portal-adicionar-campo"
        onClick={() => onMudar([...linhas, LINHA_VAZIA])}
      >
        {t.formulario.adicionarCampo}
      </Button>
    </fieldset>
  );
}
